import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    getFirestore,
    limit,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    startAfter,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { createSecondaryAuthUser } from './secondaryAuthService';
import { staffDocPath } from '@constants/firestorePaths';
import { upsertTenantStaff } from './staffService';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

function stripUndefinedDeep(value) {
    if (Array.isArray(value)) {
        const arr = value
            .map((v) => stripUndefinedDeep(v))
            .filter((v) => typeof v !== 'undefined');
        return arr;
    }
    if (value && typeof value === 'object') {
        if (typeof value?.toDate === 'function') return value;
        const next = {};
        for (const [k, v] of Object.entries(value)) {
            const cleaned = stripUndefinedDeep(v);
            if (typeof cleaned === 'undefined') continue;
            if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0) continue;
            next[k] = cleaned;
        }
        if (Object.keys(next).length === 0) return undefined;
        return next;
    }
    if (typeof value === 'undefined') return undefined;
    return value;
}

function normalizeFirestoreValue(value) {
    if (!value) return value;
    if (typeof value?.toDate === 'function') {
        const d = value.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    }
    return value;
}

function normalizeStaffProfile(profile) {
    if (!profile || typeof profile !== 'object') return profile;
    return {
        ...profile,
        dateOfBirth: normalizeFirestoreValue(profile.dateOfBirth),
        currentPosition: profile.currentPosition
            ? {
                ...profile.currentPosition,
                joiningDate: normalizeFirestoreValue(profile.currentPosition.joiningDate),
            }
            : profile.currentPosition,
    };
}

export function subscribeTenantStaff({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = collection(db(), 'staff', tenantId, 'staff');
    const q = query(baseRef, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export async function fetchStaffKpis({ tenantId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const rootRef = doc(db(), 'staff', tenantId);
    const snap = await getDoc(rootRef);
    const data = snap.exists() ? snap.data() : {};
    return {
        totalStaff: data.totalStaff ?? 0,
        activeStaff: data.activeStaff ?? 0,
        inactiveStaff: data.inactiveStaff ?? 0,
    };
}

export async function fetchTenantStaffPage({ tenantId, pageSize = 10, cursor = null }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const baseRef = collection(db(), 'staff', tenantId, 'staff');
    const countSnap = await getCountFromServer(baseRef);
    const q = cursor
        ? query(baseRef, orderBy('createdAt', 'desc'), startAfter(cursor), limit(pageSize))
        : query(baseRef, orderBy('createdAt', 'desc'), limit(pageSize));
    const snap = await getDocs(q);
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { rows, nextCursor, total: countSnap.data().count || 0 };
}

/**
 * Creates a staff member:
 * - Auth user via secondary auth
 * - staff/{tenantId}/staff/{uid} (plus lookup + analytics via upsertTenantStaff)
 * - merges the old-app profile keys into the same staff document
 */
export async function createStaffMember({ tenantId, email, password, profile }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!email || !password) throw new Error('Missing email or password');

    const created = await createSecondaryAuthUser({ email, password });
    const staffUid = created.uid;

    await upsertTenantStaff({
        tenantId,
        staffId: staffUid,
        email: created.email || email,
        role: profile?.role || 'clerk',
        permissions: 'all',
        status: profile?.currentPosition?.status || 'active',
    });

    const staffRef = doc(db(), staffDocPath(tenantId, staffUid));
    const normalized = stripUndefinedDeep(stripUndefined({
        ...normalizeStaffProfile(profile),
        staffId: profile?.staffId || null,
        email: created.email || email,
        uid: staffUid,
        tenantId,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
    }));

    await runTransaction(db(), async (tx) => {
        tx.set(staffRef, normalized || {}, { merge: true });
    });

    return { staffUid, email: created.email || email };
}

export async function updateStaffProfile({ tenantId, staffId, patch }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!staffId) throw new Error('Missing staffId');
    const staffRef = doc(db(), staffDocPath(tenantId, staffId));
    const normalized = stripUndefinedDeep(stripUndefined({
        ...normalizeStaffProfile(patch),
        updatedAt: serverTimestamp(),
    }));
    await runTransaction(db(), async (tx) => {
        tx.set(staffRef, normalized || {}, { merge: true });
    });
    return { staffId };
}

